import {
  Icons,
  mergeSx,
  SxProps,
  Typography,
  Box,
  Stack,
  tokens,
} from '@cast/design-system';
import { useOrganizations } from '@cast/shared-organization-context-data-access';

import { useUser } from '@cast/infrastructure-auth';
import { useAccessControl } from '@cast/infrastructure-abilities';

export const ESCALATION_HEADER_HEIGHT = 22;

type EscalationPrivilegeHeaderProps = {
  sx?: SxProps;
};

export const EscalationPrivilegeHeader = ({
  sx,
}: EscalationPrivilegeHeaderProps) => {
  const user = useUser();
  const { currentOrganization } = useOrganizations();
  const { subjectAccessData } = useAccessControl({
    subjectId: user.id,
    excludeOrphanedClusters: false,
  });

  if (!currentOrganization || currentOrganization.organizationMember) {
    return null;
  }

  const roleName =
    subjectAccessData?.organizations[currentOrganization.id]?.access.roleName ??
    'Viewer';
  const isViewer = roleName === 'Viewer';

  return (
    <Box
      className="escalation-header"
      data-testid="escalation-privilege-header"
      sx={mergeSx(
        {
          height: ESCALATION_HEADER_HEIGHT,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          backgroundColor: isViewer
            ? tokens.theme.bg.categorical.greyStrong
            : tokens.theme.bg.categorical.aquaStrong,
          color: tokens.theme.fg.structural.primaryOnInverse,
          px: 24,
          position: 'sticky',
          top: 0,
          zIndex: 4,
        },
        sx
      )}
    >
      <Stack direction="row" alignItems="center" gap={4}>
        {isViewer ? <Icons.Eye size={12} /> : <Icons.PencilSimple size={12} />}
        <Typography variant="P10M">
          Privilege escalated mode: You are exploring the{' '}
          {currentOrganization?.internal ? 'internal' : 'customer'} organization
          with {roleName} role
        </Typography>
      </Stack>
    </Box>
  );
};
